import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';

const STARTUP_LOGOS = [
  'https://images.mastersunion.link/uploads/30052025/v1/Frame13213169303.webp',
  'https://images.mastersunion.link/uploads/30052025/v1/Frame13213169461.webp',
  'https://images.mastersunion.link/uploads/30052025/v1/Frame13213169471.webp',
  'https://images.mastersunion.link/uploads/30052025/v1/Frame13213169481.webp',
  'https://images.mastersunion.link/uploads/30052025/v1/Frame13213169491.webp',
  'https://images.mastersunion.link/uploads/30052025/v1/Frame13213169501.webp',
  'https://images.mastersunion.link/uploads/30052025/v1/Frame13213169511.webp',
  'https://images.mastersunion.link/uploads/30052025/v1/Frame1321316952.webp',
  'https://images.mastersunion.link/uploads/30052025/v1/Frame13213169531.webp',
  'https://images.mastersunion.link/uploads/30052025/v1/Frame1321316954.webp',
  'https://images.mastersunion.link/uploads/30052025/v1/Frame1321316955.webp',
  'https://images.mastersunion.link/uploads/30052025/v1/Frame1321316958.webp',
  'https://images.mastersunion.link/uploads/30052025/v1/Frame1321316959.webp',
  'https://images.mastersunion.link/uploads/30052025/v1/Frame1321316957.webp',
  'https://images.mastersunion.link/uploads/30052025/v1/Frame1321316960.webp',
];

const SHARK_TANK = [
  { season: 2, startup: 'Bullspree', desc: "India's favourite stock market playground for learning & investing", founder: 'Dharmil Bavishi', batch: "PGP TBM Co'21", img: 'https://images.mastersunion.link/uploads/26122025/v6/SharkTankCard.webp' },
  { season: 4, startup: 'Nexera Health', desc: 'Redefining workplace wellness for employees', founder: 'Himanshu Rajpurohit', batch: 'CEO Challenge', img: 'https://images.mastersunion.link/uploads/26122025/v1/Frame1321317467.webp' },
  { season: 4, startup: 'Hive School', desc: "Building India's first Sales School", founder: 'Nikhil Gaur', batch: "PGP TBM Co'24", img: 'https://images.mastersunion.link/uploads/26122025/v1/nikhilgaur.webp' },
  { season: 4, startup: 'Memotag', desc: 'AI-driven wearable for dementia care', founder: 'Reyansh Juneja', batch: "UG TBM Co'28", img: 'https://images.mastersunion.link/uploads/26122025/v1/memotag.webp' },
  { season: 5, startup: 'HookD', desc: "India's first ready-to-eat non-veg snacking brand", founder: 'Dia Goel', batch: "PGP TBM Co'23", img: 'https://pgptbmdemo.lovable.app/assets/hookd-lVOiJaoU.webp' },
  { season: 5, startup: 'Meta Fashion', desc: 'Phygital commerce connecting in-game discovery with real-world fashion', founder: 'Arjun Goel', batch: "UG TBM Co'28", img: 'https://pgptbmdemo.lovable.app/assets/metafashion-DtvZ85_G.webp' },
];

const STATS = [
  { value: '30+',     label: 'Startups Incubated' },
  { value: '₹480 Cr', label: 'Projected Revenue FY26' },
  { value: '₹593 Cr', label: 'Total Valuation' },
  { value: '₹1.2 Cr', label: 'Grants Disbursed' },
];

const FadeIn = ({ children, delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay, ease: [0.2, 0.8, 0.2, 1] }}
    viewport={{ once: true, margin: '-40px' }}
  >{children}</motion.div>
);

export const EntrepreneurshipSection = ({ founderFitment }) => {
  const logos     = [...STARTUP_LOGOS, ...STARTUP_LOGOS];
  const scrollRef = useRef(null);
  const scrollBy  = (dir) => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollBy({ left: dir * (scrollRef.current.offsetWidth / 3 + 12), behavior: 'smooth' });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      style={{ backgroundColor: '#ffffff', border: '1px solid rgba(0,0,0,0.08)', borderRadius: '16px', overflow: 'hidden', marginBottom: '24px', fontFamily: 'DM Sans, sans-serif', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}
    >
      {/* Header + Stats */}
      <div style={{ padding: 'clamp(28px,5vw,48px) clamp(20px,5vw,40px) 0' }}>
        <FadeIn>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '18px' }}>
            <span style={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.20em', textTransform: 'uppercase', color: '#E8A020' }}>Entrepreneurship</span>
            <div style={{ flex: 1, height: '1px', backgroundColor: 'rgba(0,0,0,0.08)' }} />
          </div>
          <h2 style={{ fontSize: 'clamp(22px,3.5vw,38px)', fontWeight: 800, color: '#111111', letterSpacing: '-0.025em', lineHeight: 1.1, marginBottom: '16px' }}>
            Where Founders Are{' '}
            <span style={{ fontFamily: 'Cormorant Garamond, serif', fontStyle: 'italic', fontWeight: 500, color: '#E8A020' }}>Built, Not Just Funded</span>
          </h2>
          <p style={{ fontSize: 'clamp(13px,1.6vw,15px)', color: 'rgba(0,0,0,0.55)', lineHeight: 1.75, maxWidth: '680px', marginBottom: founderFitment?.founder_potential ? '24px' : '32px' }}>
            If you've ever had the itch to build something of your own, Masters' Union is the place that will push you to do it. Not just dream or post, actually build. You get the ecosystem, mentorship, grants and investor access to turn that idea into a startup.
          </p>

          {/* ── Founder Fitment (personalised) ── */}
          {founderFitment?.founder_potential && (
            <div style={{ backgroundColor: 'rgba(232,160,32,0.06)', border: '1.5px solid rgba(232,160,32,0.18)', borderRadius: '12px', padding: '18px 20px', marginBottom: '32px' }}>
              <p style={{ fontSize: '14px', fontWeight: 700, color: '#9A6200', textTransform: 'uppercase', letterSpacing: '0.10em', marginBottom: '8px' }}>Your Founder Fitment</p>
              <p style={{ fontSize: '14.5px', color: 'rgba(0,0,0,0.65)', lineHeight: 1.65, marginBottom: founderFitment.industries?.length ? '16px' : 0 }}>
                {founderFitment.founder_potential}
              </p>
              {founderFitment.industries?.length > 0 && (
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  {founderFitment.industries.map((ind, i) => (
                    <div key={i} style={{ flex: '1 1 180px', backgroundColor: '#ffffff', border: '1px solid rgba(0,0,0,0.08)', borderRadius: '10px', padding: '12px 14px' }}>
                      <p style={{ fontSize: '14px', fontWeight: 700, color: '#111111', margin: '0 0 4px' }}>{ind.name}</p>
                      <p style={{ fontSize: '14.5px', color: 'rgba(0,0,0,0.55)', lineHeight: 1.5, margin: 0 }}>{ind.why}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </FadeIn>

        {/* Stats */}
        <FadeIn delay={0.1}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px', marginBottom: '44px' }} className="entreStats">
            {STATS.map((s, i) => (
              <div key={i} style={{ backgroundColor: 'rgba(232,160,32,0.06)', border: '1.5px solid rgba(232,160,32,0.20)', borderRadius: '12px', padding: '18px 16px', textAlign: 'center' }}>
                <div style={{ fontSize: 'clamp(20px,2.8vw,30px)', fontWeight: 900, color: '#C47A00', letterSpacing: '-0.03em', lineHeight: 1, marginBottom: '5px' }}>{s.value}</div>
                <div style={{ fontSize: '14px', color: 'rgba(0,0,0,0.45)', fontWeight: 500 }}>{s.label}</div>
              </div>
            ))}
          </div>
        </FadeIn>
      </div>

      {/* Logo carousel */}
      <FadeIn delay={0.12}>
        <div style={{ paddingLeft: 'clamp(20px,5vw,40px)', marginBottom: '40px' }}>
          <p style={{ fontSize: '14px', fontWeight: 700, color: 'rgba(0,0,0,0.50)', letterSpacing: '0.04em', marginBottom: '16px' }}>Top Startups</p>
        </div>
        <div style={{ overflow: 'hidden', width: '100%' }}>
          <div className="logos-track" style={{ display: 'flex', gap: '10px', width: 'max-content', animation: 'logoScroll 28s linear infinite' }}>
            {logos.map((src, i) => (
              <div key={i} style={{ width: '120px', height: '72px', flexShrink: 0, backgroundColor: '#ffffff', borderRadius: '10px', border: '1px solid rgba(0,0,0,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '10px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                <img src={src} alt={`s-${i}`} style={{ width: '100%', height: '100%', objectFit: 'contain' }} onError={e => { e.target.style.display = 'none'; }} />
              </div>
            ))}
          </div>
        </div>
      </FadeIn>

      {/* Shark Tank */}
      <div style={{ padding: '0 clamp(20px,5vw,40px)' }}>
        <FadeIn delay={0.1}>
          <div style={{ marginTop: '44px', marginBottom: '20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
            <div>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', backgroundColor: 'rgba(255,199,0,0.12)', border: '1px solid rgba(255,199,0,0.35)', borderRadius: '9999px', padding: '5px 14px', marginBottom: '7px' }}>
                <span style={{ fontSize: '10px', fontWeight: 700, color: '#9A6200', letterSpacing: '0.12em', textTransform: 'uppercase' }}>📺 As Seen On</span>
                <span style={{ fontSize: '14.5px', fontWeight: 800, color: '#9A6200', letterSpacing: '0.08em' }}>SHARK TANK INDIA</span>
              </div>
              <p style={{ fontSize: '14px', color: 'rgba(0,0,0,0.40)', margin: 0 }}>MU students on India's biggest startup stage</p>
            </div>
            <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
              {[{ dir: -1, Icon: ChevronLeft }, { dir: 1, Icon: ChevronRight }].map(({ dir, Icon }) => (
                <button key={dir} onClick={() => scrollBy(dir)} style={{ width: '36px', height: '36px', borderRadius: '50%', backgroundColor: '#f5f5f5', border: '1px solid rgba(0,0,0,0.10)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background 0.2s' }}
                  onMouseEnter={e => e.currentTarget.style.backgroundColor = '#eeeeee'}
                  onMouseLeave={e => e.currentTarget.style.backgroundColor = '#f5f5f5'}>
                  <Icon size={16} color="rgba(0,0,0,0.50)" />
                </button>
              ))}
            </div>
          </div>
        </FadeIn>

        <div ref={scrollRef} className="shark-scroll" style={{ display: 'flex', gap: '12px', overflowX: 'auto', scrollSnapType: 'x mandatory', WebkitOverflowScrolling: 'touch', paddingBottom: '4px', marginBottom: '44px' }}>
          {SHARK_TANK.map((s, i) => (
            <div key={i} style={{ flex: '0 0 calc(33.33% - 8px)', minWidth: '260px', scrollSnapAlign: 'start', borderRadius: '14px', overflow: 'hidden', backgroundColor: '#ffffff', border: '1px solid rgba(0,0,0,0.09)', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', display: 'flex', flexDirection: 'column' }}>
              <div style={{ position: 'relative', height: '240px', flexShrink: 0, overflow: 'hidden', backgroundColor: '#1A1A1A' }}>
                <img src={s.img} alt={s.founder}
                  style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top center', filter: 'grayscale(100%) brightness(0.80)', transition: 'filter 0.35s ease' }}
                  onMouseEnter={e => { e.target.style.filter = 'grayscale(0%) brightness(1)'; }}
                  onMouseLeave={e => { e.target.style.filter = 'grayscale(100%) brightness(0.80)'; }}
                  onError={e => { e.target.style.display = 'none'; e.target.parentNode.style.background = 'linear-gradient(160deg,#1A1A14,#0A0A0A)'; }}
                />
                <div style={{ position: 'absolute', top: '10px', left: '10px', backgroundColor: '#FFC700', color: '#000', fontSize: '9px', fontWeight: 900, padding: '4px 8px', borderRadius: '4px', letterSpacing: '0.08em', textTransform: 'uppercase' }}>SEASON {s.season}</div>
              </div>
              <div style={{ padding: '14px 16px 16px', display: 'flex', flexDirection: 'column', flex: 1, backgroundColor: '#ffffff' }}>
                <div style={{ display: 'inline-block', backgroundColor: '#FFC700', color: '#000', fontSize: '14px', fontWeight: 800, padding: '5px 11px', borderRadius: '6px', marginBottom: '10px' }}>{s.startup}</div>
                <p style={{ fontSize: '14px', color: 'rgba(0,0,0,0.55)', lineHeight: 1.55, margin: '0 0 12px', flex: 1 }}>{s.desc}</p>
                <div style={{ borderTop: '1px solid rgba(0,0,0,0.07)', paddingTop: '10px' }}>
                  <p style={{ fontSize: '14px', fontWeight: 700, color: '#111111', margin: '0 0 2px' }}>{s.founder}</p>
                  <p style={{ fontSize: '14px', color: 'rgba(0,0,0,0.38)', margin: 0 }}>{s.batch}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Founder Fellowship */}
      <FadeIn delay={0.1}>
        <div style={{ margin: '0 clamp(20px,5vw,40px)', marginBottom: 'clamp(28px,5vw,48px)', backgroundColor: 'rgba(232,160,32,0.05)', border: '1.5px solid rgba(232,160,32,0.20)', borderRadius: '16px', padding: 'clamp(22px,4vw,34px)', position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', right: '-40px', top: '-40px', width: '180px', height: '180px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(232,160,32,0.10) 0%, transparent 70%)', pointerEvents: 'none' }} />
          <p style={{ fontSize: '14px', fontWeight: 600, color: 'rgba(0,0,0,0.38)', letterSpacing: '0.08em', marginBottom: '8px', textTransform: 'uppercase' }}>That's Not It...</p>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: '24px' }}>
            <div style={{ flex: 1, minWidth: '260px' }}>
              <h3 style={{ fontSize: 'clamp(20px,2.5vw,28px)', fontWeight: 800, color: '#111111', letterSpacing: '-0.02em', marginBottom: '14px', lineHeight: 1.2 }}>
                Founder{' '}<span style={{ fontFamily: 'Cormorant Garamond, serif', fontStyle: 'italic', fontWeight: 500, color: '#E8A020' }}>Fellowship</span>
              </h3>
              <p style={{ fontSize: 'clamp(13px,1.5vw,15px)', color: 'rgba(0,0,0,0.58)', lineHeight: 1.75, marginBottom: '20px', maxWidth: '540px' }}>
                We believe in bold ideas and fearless founders. Every year, ~15% of the batch go on to become entrepreneurs. If you choose to build your startup instead of taking campus placements, you can apply and receive{' '}
                <strong style={{ color: '#C47A00' }}>₹50,000 per month for up to one year</strong> — so you focus on scaling the venture, not finances.
              </p>
              {/* Changed button text to "Apply to PGP TBM" */}
              <a href="https://mastersunion.org/pgp-technology-and-business-management" target="_blank" rel="noopener noreferrer"
                style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '11px 22px', backgroundColor: '#111111', border: 'none', borderRadius: '9999px', cursor: 'pointer', textDecoration: 'none', fontFamily: 'DM Sans, sans-serif', fontSize: '14px', fontWeight: 700, color: '#ffffff', transition: 'background-color 0.2s ease, transform 0.15s ease' }}
                onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#E8A020'; e.currentTarget.style.transform = 'scale(1.02)'; }}
                onMouseLeave={e => { e.currentTarget.style.backgroundColor = '#111111'; e.currentTarget.style.transform = 'scale(1)'; }}>
                Apply to PGP TBM <ArrowRight size={13} />
              </a>
            </div>
            <div style={{ flexShrink: 0, backgroundColor: '#ffffff', border: '1.5px solid rgba(232,160,32,0.30)', borderRadius: '14px', padding: '20px 24px', textAlign: 'center', minWidth: '160px', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
              <div style={{ fontSize: 'clamp(26px,4vw,40px)', fontWeight: 900, color: '#C47A00', letterSpacing: '-0.03em', lineHeight: 1, marginBottom: '5px' }}>₹50K</div>
              <div style={{ fontSize: '14.5px', color: 'rgba(0,0,0,0.50)', marginBottom: '3px', fontWeight: 500 }}>per month</div>
              <div style={{ fontSize: '14px', color: 'rgba(0,0,0,0.35)', fontWeight: 500 }}>for up to 1 year</div>
              <div style={{ marginTop: '10px', height: '1px', backgroundColor: 'rgba(232,160,32,0.18)' }} />
              <div style={{ marginTop: '10px', fontSize: '14px', color: 'rgba(0,0,0,0.38)' }}>~15% of each batch</div>
              <div style={{ fontSize: '14px', color: 'rgba(0,0,0,0.38)' }}>become founders</div>
            </div>
          </div>
        </div>
      </FadeIn>
    </motion.div>
  );
};
